import React from 'react';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import alertToast from 'Util/ToastifyUtils';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextArea } from 'Views/Components/TextArea/TextArea';
import MessageBox from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

/** Matches the server's cap, so a reason that would be rejected cannot be typed past it. */
const MaxReasonLength = 500;

interface ConfirmPreferenceChangeOptions {
	customerName?: string;
	/** The changes about to be applied, one per line, in the words the staff member will read. */
	summary: string[];
	/** Runs on confirm. Throwing keeps the modal open with the error shown. */
	onConfirm: (reason: string) => Promise<void>;
}

interface ReasonModel {
	reason: string;
	error: string;
}

const ConfirmContent = observer(
	({ customerName, summary, model }: { customerName?: string; summary: string[]; model: ReasonModel }) => (
		<div className="flex flex-col gap-3">
			<MessageBox status="warning" slim>
				This changes what {customerName ? <strong>{customerName}</strong> : 'this customer'} receives
				from us. The reason below is added to their notes and to the audit log.
			</MessageBox>

			<div>
				<div className="text-xs font-semibold uppercase text-gray-500">Changes</div>
				<ul className="mt-1 list-disc pl-5 text-sm">
					{summary.map(line => (
						<li key={line}>{line}</li>
					))}
				</ul>
			</div>

			<div className="form-group">
				<TextArea
					id="communication-preference-reason"
					label="Reason for this change"
					model={model}
					modelProperty="reason"
					isRequired
					placeholder="e.g. Customer rang and asked to stop reminder texts"
					onAfterChange={event => {
						runInAction(() => {
							model.reason = event.target.value;
							if (model.error) {
								model.error = '';
							}
						});
					}}
				/>
				<div className="character-count">
					{model.reason.length}/{MaxReasonLength}
				</div>
				{model.error && <div className="invalid-feedback">{model.error}</div>}
			</div>
		</div>
	),
);

/**
 * Confirms a staff change to a customer's communication preferences, and collects the reason the
 * server requires.
 *
 * <p>The reason is mandatory here as well as on the server. The client check is what makes it a
 * usable form — the server's is what makes it a rule, since a request built by hand would otherwise
 * change a customer's preferences with no record of who decided to.</p>
 *
 * <p>Resolves when the change has been saved, and stays open on failure with the error shown rather
 * than closing over a save that did not happen.</p>
 */
export async function confirmPreferenceChange({
	customerName,
	summary,
	onConfirm,
}: ConfirmPreferenceChangeOptions): Promise<boolean> {
	const model = observable<ReasonModel>({ reason: '', error: '' });

	return newConfirmModal(
		'Confirm preference change',
		<ConfirmContent customerName={customerName} summary={summary} model={model} />,
		{
			cancelText: 'Back',
			confirmText: 'Save changes',
			resolveOnCancel: false,
			onBeforeConfirm: async () => {
				const reason = model.reason.trim();

				if (reason.length === 0) {
					runInAction(() => {
						model.error = 'A reason is required.';
					});
					return false;
				}

				if (reason.length > MaxReasonLength) {
					runInAction(() => {
						model.error = `The reason must be ${MaxReasonLength} characters or fewer.`;
					});
					return false;
				}

				try {
					await onConfirm(reason);
					alertToast('Communication preferences updated', 'success');
					return true;
				} catch (error: any) {
					// Kept open rather than closed on failure: closing would leave the staged edits
					// looking saved when nothing was written.
					runInAction(() => {
						model.error = error?.response?.data ?? 'Could not save the change. Try again.';
					});
					return false;
				}
			},
		},
	).catch(() => false);
}
